/**
 * Customer rules shared by server and apps: loyalty points and segments.
 * Points are whole numbers. Settings: Settings → Customers & reports.
 */

/** Points earned for paying `amountPaise` (points-paid part excluded by the caller). */
export function pointsEarned(amountPaise, loyalty) {
  if (!loyalty?.enabled || !loyalty.earnPerRupees || amountPaise <= 0) return 0;
  return Math.floor(amountPaise / (loyalty.earnPerRupees * 100));
}

/** Points needed to pay `amountPaise` (rounded up: never pay more than the points cover). */
export function pointsFor(amountPaise, loyalty) {
  return Math.ceil(amountPaise / Math.max(1, loyalty.pointValuePaise));
}

/** Rupee value of points (paise). */
export const pointsValue = (points, loyalty) => points * Math.max(1, loyalty.pointValuePaise);

/** The most a customer can pay with points on a bill of `duePaise` (0 if below the minimum). */
export function maxRedeemPaise(balance, duePaise, loyalty) {
  if (!loyalty?.enabled || balance < loyalty.minRedeemPoints || balance <= 0) return 0;
  return Math.min(duePaise, pointsValue(balance, loyalty));
}

export function redeemCheck({ balance, amountPaise, loyalty }) {
  if (!loyalty?.enabled) return { ok: false, error: 'Loyalty points are switched off.' };
  if (balance < loyalty.minRedeemPoints)
    return { ok: false, error: `Points can be used from ${loyalty.minRedeemPoints} points (balance ${balance}).` };
  const pts = pointsFor(amountPaise, loyalty);
  if (pts > balance) return { ok: false, error: `Not enough points: needs ${pts}, balance ${balance}.` };
  return { ok: true, points: pts };
}

const DAY = 86_400_000;

/**
 * Segments a customer belongs to (several can apply), and the main one for badges.
 * customer: { stats: { visits, totalSpendPaise, firstVisitAt, lastVisitAt } }
 */
export function segmentsOf(customer, rules, now = new Date()) {
  const s = customer.stats ?? {};
  const out = [];
  const since = (d) => (d ? (now - new Date(d)) / DAY : Infinity);
  if (s.firstVisitAt && since(s.firstVisitAt) <= rules.newDays) out.push('new');
  const lapsed = s.lastVisitAt && since(s.lastVisitAt) > rules.lapsedDays;
  if (lapsed) out.push('lapsed');
  if (!lapsed && (s.visits ?? 0) >= rules.regularVisits) out.push('regular');
  if ((s.totalSpendPaise ?? 0) >= rules.highValueSpendPaise) out.push('high_value');
  const primary = ['high_value', 'lapsed', 'regular', 'new'].find((k) => out.includes(k)) ?? 'occasional';
  return { segments: out, primary };
}

/** "MM-DD" of a date in a time zone (birthday / anniversary matching). */
export function monthDay(date, tz = 'Asia/Kolkata') {
  const parts = new Intl.DateTimeFormat('en-CA', { timeZone: tz, month: '2-digit', day: '2-digit' }).formatToParts(
    new Date(date),
  );
  return `${parts.find((p) => p.type === 'month').value}-${parts.find((p) => p.type === 'day').value}`;
}
